import { Routes } from '@angular/router';
import { authGuard } from './core/guard/auth.route';
import { loginGuard } from './core/guard/login.guard';
import { permissionGuard } from './core/guard/permission.route';

export const routes: Routes = [
  {
    path: 'version',
    loadComponent: () =>
      import('./features/version/version.component').then(
        (c) => c.VersionComponent,
      ),
    data: {
      title: 'Version Info',
    },
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./features/login/login.component').then((c) => c.LoginComponent),
    canActivate: [loginGuard],
    data: {
      title: 'Log in',
      breadcrumb: 'Log in',
    },
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./features/forgot-password/forgot-password.component').then(
        (c) => c.ForgotPasswordComponent,
      ),
    canActivate: [loginGuard],
    data: {
      title: 'Forgot Password',
      breadcrumb: 'Forgot Password',
    },
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./features/reset-password/reset-password.component').then(
        (c) => c.ResetPasswordComponent,
      ),
    canActivate: [loginGuard],
    data: {
      title: 'Reset Password',
      breadcrumb: 'Reset Password',
    },
  },
  {
    path: 'verify-email',
    loadComponent: () =>
      import('./features/verify-email/verify-email.component').then(
        (c) => c.VerifyEmailComponent,
      ),
    canActivate: [loginGuard],
    data: {
      title: 'Verify Email',
      breadcrumb: 'Verify Email',
    },
  },
  {
    path: 'check-email',
    loadComponent: () =>
      import('./features/check-email/check-email.component').then(
        (c) => c.CheckEmailComponent,
      ),
    canActivate: [loginGuard],
    data: {
      title: 'Check Email',
      breadcrumb: 'Check Email',
    },
  },
  {
    path: 'register',
    loadComponent: () =>
      import('./features/registration/registration.component').then(
        (c) => c.RegistrationComponent,
      ),
    canActivate: [loginGuard],
    data: {
      title: 'Registration',
      breadcrumb: 'Registration',
    },
  },
  {
    path: '',
    loadComponent: () =>
      import('./shell/shell.component').then((c) => c.ShellComponent),
    canActivate: [authGuard],
    data: {
      title: 'Elementrix',
      breadcrumb: 'Elementrix',
    },
    children: [
      {
        path: '',
        redirectTo: 'domains',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(
            (c) => c.DashboardComponent,
          ),
        canActivate: [authGuard, permissionGuard],
        data: {
          title: 'Dashboard',
          breadcrumb: 'Dashboard',
          section: 'Administration',
        },
      },
      {
        path: 'pricing',
        loadComponent: () =>
          import('./features/pricing/pricing.component').then(
            (c) => c.PricingComponent,
          ),
        canActivate: [authGuard, permissionGuard],
        data: {
          title: 'Pricing Management',
          breadcrumb: 'Pricing',
          section: 'Administration',
        },
      },
      {
        path: 'user-management',
        loadChildren: () =>
          import('./features/user-management/routes').then(
            (m) => m.userManagementRoutes,
          ),
        canActivate: [authGuard, permissionGuard],
        data: {
          title: 'Users',
          breadcrumb: 'Users',
          section: 'Administration',
        },
      },
      {
        path: 'configuration',
        loadChildren: () =>
          import('./features/configuration/routes').then(
            (m) => m.configurationRoutes,
          ),
        canActivate: [authGuard, permissionGuard],
        data: {
          title: 'Configuration',
          breadcrumb: 'Configuration',
          section: 'Administration',
        },
      },

      {
        path: 'domains',
        loadChildren: () =>
          import('./features/domain-management/routes').then(
            (m) => m.domainManagementRoutes,
          ),
        canActivate: [authGuard],
        data: {
          title: 'Domains',
          breadcrumb: 'Domains',
          section: 'Discover',
        },
      },
      {
        path: 'search-results',
        loadComponent: () =>
          import('./features/search-results/search-results.component').then(
            (c) => c.SearchResultsComponent,
          ),
        canActivate: [authGuard],
        data: {
          title: 'Search Results',
          breadcrumb: 'Search Results',
          section: 'Elementrix',
        },
      },

      {
        path: 'business-glossary',
        loadChildren: () =>
          import('./features/business-glossary-management/routes').then(
            (m) => m.businessGlossaryManagementRoutes,
          ),
        canActivate: [authGuard, permissionGuard],
        data: {
          title: 'Semantic Registry',
          breadcrumb: 'Semantic Registry',
          section: 'Discover',
        },
      },

      {
        path: 'marketplace',
        loadChildren: () =>
          import('./features/marketplace/routes').then(
            (m) => m.marketplaceRoutes,
          ),
        canActivate: [authGuard],
        data: {
          title: 'Marketplace',
          breadcrumb: 'Marketplace',
          section: 'Discover',
        },
      },
      {
        path: 'data-products',
        loadChildren: () =>
          import('./features/data-product/routes').then(
            (m) => m.dataProductRoutes,
          ),
        canActivate: [authGuard],
        data: {
          title: 'Data Products',
          breadcrumb: 'Data Products',
          section: 'Discover',
        },
      },
      {
        path: 'my-data-products',
        loadChildren: () =>
          import('./features/data-product/routes').then(
            (m) => m.dataProductRoutes,
          ),
        canActivate: [authGuard],
        data: {
          title: 'My Data Products',
          breadcrumb: 'My Data Products',
          section: 'Discover',
        },
      },
      {
        path: 'reference-data',
        loadChildren: () =>
          import('./features/reference-data-registry/routes').then(
            (m) => m.referenceDataRegistryRoutes,
          ),
        canActivate: [authGuard],
        data: {
          title: 'Reference Data Registry',
          breadcrumb: 'Reference Data',
          section: 'Discover',
        },
      },
      {
        path: 'my-business-glossary',
        loadChildren: () =>
          import('./features/business-glossary-management/routes').then(
            (m) => m.businessGlossaryManagementRoutes,
          ),
        canActivate: [authGuard],
        data: {
          title: 'My Semantic Registry',
          breadcrumb: 'My Semantic Registry',
          section: 'Discover',
        },
      },
      {
        path: 'profile',
        loadChildren: () =>
          import('./features/profile/routes').then((m) => m.profileRoutes),
        canActivate: [authGuard],
        data: {
          title: 'My Profile',
          breadcrumb: 'My Profile',
        },
      },

      {
        path: 'settings',
        loadComponent: () =>
          import('./features/profile/components/settings/settings.component').then(
            (m) => m.SettingsComponent,
          ),
        canActivate: [authGuard, permissionGuard],
        data: {
          title: 'Settings',
          breadcrumb: 'Settings',
          section: 'Administration',
        },
      },
      {
        path: 'my-applications',
        loadChildren: () =>
          import('./features/applications/routes').then(
            (m) => m.applicationsRoutes,
          ),
        canActivate: [authGuard],
        data: {
          title: 'My Applications',
          breadcrumb: 'My Applications',
          section: 'Applications',
        },
      },
      {
        path: 'applications',
        loadChildren: () =>
          import('./features/applications/routes').then(
            (m) => m.applicationsRoutes,
          ),
        canActivate: [authGuard, permissionGuard],
        data: {
          title: 'Application Management',
          breadcrumb: 'Application Management',
          section: 'Administration',
        },
      },

      {
        path: 'roles',
        loadChildren: () =>
          import('./features/roles/routes').then((m) => m.rolesRoutes),
        canActivate: [authGuard, permissionGuard],
        data: {
          title: 'Roles Management',
          breadcrumb: 'Roles',
          section: 'Administration',
        },
      },
      {
        path: 'organizations',
        loadChildren: () =>
          import('./features/organizations/routes').then(
            (m) => m.organizationRoutes,
          ),
        canActivate: [authGuard, permissionGuard],
        data: {
          title: 'Organizations',
          breadcrumb: 'Organizations',
          section: 'Administration',
        },
      },

      {
        path: 'my-requests',
        loadChildren: () =>
          import('./features/my-requests/routes').then(
            (m) => m.myRequestsRoutes,
          ),
        canActivate: [authGuard],
        data: {
          title: 'My Requests',
          breadcrumb: 'My Requests',
          section: 'Requests',
        },
      },

      {
        path: 'my-purchased-products',
        loadChildren: () =>
          import('./features/my-purchased-products/routes').then(
            (m) => m.myPurchasedProductsRoutes,
          ),
        canActivate: [authGuard, permissionGuard],
        data: {
          title: 'My Purchased Products',
          breadcrumb: 'My Purchased Products',
          section: 'Manage',
        },
      },
      {
        path: 'data-sources',
        loadChildren: () =>
          import('./features/data-sources/routes').then(
            (m) => m.dataSourcesRoutes,
          ),
        canActivate: [authGuard, permissionGuard],
        data: {
          title: 'Data Sources',
          breadcrumb: 'Data Sources',
          section: 'Discover',
        },
      },
      {
        path: 'my-data-sources',
        loadChildren: () =>
          import('./features/data-sources/routes').then(
            (m) => m.dataSourcesRoutes,
          ),
        canActivate: [authGuard, permissionGuard],
        data: {
          title: 'My Data Sources',
          breadcrumb: 'My Data Sources',
          section: 'Discover',
          mine: true,
        },
      },
      {
        path: 'health-dashboard',
        loadChildren: () =>
          import('./features/health-dashboard/routes').then(
            (m) => m.healthDashboardRoutes,
          ),
        canActivate: [authGuard, permissionGuard],
        data: {
          title: 'Health Dashboard',
          breadcrumb: 'Health Dashboard',
          section: 'Manage',
        },
      },
      {
        path: 'demo-config',
        loadChildren: () =>
          import('./features/demo-config/demo-config.routes').then(
            (m) => m.demoConfigRoutes,
          ),
        canActivate: [authGuard],
        data: {
          title: 'Demo Configuration',
          breadcrumb: 'Demo Config',
        },
      },
    ],
  },

  {
    path: 'error/401',
    loadComponent: () =>
      import('./shared/components/error-handler/error-handler.component').then(
        (c) => c.ErrorHandlerComponent,
      ),
    data: {
      title: 'Unauthorized',
      errorCode: 401,
    },
  },
  {
    path: 'error/403',
    loadComponent: () =>
      import('./shared/components/error-handler/error-handler.component').then(
        (c) => c.ErrorHandlerComponent,
      ),
    data: {
      title: 'Forbidden',
      errorCode: 403,
    },
  },
  {
    path: 'error/500',
    loadComponent: () =>
      import('./shared/components/error-handler/error-handler.component').then(
        (c) => c.ErrorHandlerComponent,
      ),
    data: {
      title: 'Internal Server Error',
      errorCode: 500,
    },
  },
  {
    path: '**',
    loadComponent: () =>
      import('./shared/components/not-found/not-found.component').then(
        (c) => c.NotFoundComponent,
      ),
    data: {
      title: 'Page Not Found',
    },
  },
];
